$component-class: 'sn-form-type-1';

// Origin (SignNow) breakpoints — kept explicit to match the source pixel-for-pixel.
$bp-tablet: 640px;
$bp-desktop: 960px;
$bp-lg-desktop: 1280px;

// Field tokens straight from the origin (_forms.styl) where no design token matches.
$placeholder-color: #667988;
$divider-color: #81919d;

.#{$component-class} {
  display: inline-block;

  // ── Trigger ──────────────────────────────────────────────
  &__trigger-wrap {
    display: inline-block;
  }

  // Fallback trigger is rendered as an <me-button>, which provides its own
  // styling; no bespoke button styles are needed here.

  // ── Modal shell + animation ──────────────────────────────
  &__modal {
    display: none;

    &.is-open {
      display: block;
    }
  }

  &__overlay {
    position: fixed;
    inset: 0;
    z-index: 1001;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    overflow: hidden auto;
    background: rgb(0 0 0 / 60%);
    -webkit-tap-highlight-color: transparent;
  }

  &__container {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    width: 100%;
    min-height: 100%;
  }

  &__form-container {
    position: relative;
    width: 100%;
    max-width: 608px;
    margin: 0 auto;
    padding: 32px 16px;
    background-color: var(--background-color-primary, #fff);
    border-radius: var(--radius-size-md, 8px);
    box-shadow: 0 15px 25px 0 rgb(0 0 0 / 10%);

    @include above($bp-tablet) {
      margin-top: 5vh;
      margin-bottom: 32px;
      padding: 55px 70px 32px;
    }

    @include above($bp-desktop) {
      margin-top: 10vh;
      padding: 55px 70px 50px;
    }

    @include above($bp-lg-desktop) {
      margin-top: 12vh;
    }
  }

  // Enter / exit animations mirror the origin micromodal "slide" variant.
  &__modal[aria-hidden='false'] {
    .#{$component-class}__overlay {
      animation: #{$component-class}-fade-in 0.25s cubic-bezier(0, 0, 0.2, 1);
    }

    .#{$component-class}__form-container {
      animation: #{$component-class}-slide-in 0.25s cubic-bezier(0, 0, 0.2, 1);
    }
  }

  &__modal[aria-hidden='true'] {
    .#{$component-class}__overlay {
      animation: #{$component-class}-fade-out 0.25s cubic-bezier(0, 0, 0.2, 1);
    }

    .#{$component-class}__form-container {
      animation: #{$component-class}-slide-out 0.25s cubic-bezier(0, 0, 0.2, 1);
    }
  }

  // ── Header ───────────────────────────────────────────────
  &__header {
    position: relative;
    display: block;
    text-align: center;
  }

  &__title {
    max-width: 255px;
    margin: 0 auto 10px;
    font-weight: 700;
    font-size: 18px;
    line-height: 1.42;
    color: var(--text-color-primary, #2c2c2c);

    @include above($bp-tablet) {
      max-width: 100%;
      font-size: 20px;
    }

    @include above($bp-desktop) {
      font-size: 24px;
    }
  }

  &__subtitle {
    font-size: 14px;
    line-height: 1.5;
    color: var(--text-color-primary, #2c2c2c);

    a {
      color: var(--link-color-brand-primary, #0777cf);
    }
  }

  &__close {
    position: absolute;
    top: -11px;
    right: -4px;
    z-index: 99;
    width: 20px;
    height: 20px;
    padding: 0;
    background: transparent;
    border: 0;
    cursor: pointer;

    @include above($bp-desktop) {
      top: -32px;
      right: -47px;
    }

    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 9px;
      left: 0;
      width: 20px;
      height: 2px;
      background: var(--text-color-primary, #2c2c2c);
    }

    &::before {
      transform: rotate(45deg);
    }

    &::after {
      transform: rotate(-45deg);
    }
  }

  // ── Form ─────────────────────────────────────────────────
  &__content {
    margin-top: 35px;
  }

  &__asterisk {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    font-size: 12px;
    line-height: 1.5;
    color: $placeholder-color;

    &::before {
      content: '';
      flex: 0 0 auto;
      width: 16px;
      height: 16px;
      margin-right: 8px;
      background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none'%3E%3Ccircle cx='8' cy='8' r='6.5' stroke='%23667988'/%3E%3Cpath fill='%23667988' d='M7.25 6.75h1.5V12h-1.5zM8 4a.9.9 0 1 1 0 1.8A.9.9 0 0 1 8 4z'/%3E%3C/svg%3E") no-repeat center / contain;
    }
  }

  // Shared form elements (validation + per-field error messages handled by
  // form-handler-type-1, mirroring sn-hero-header-type-4).
  me-input,
  me-select,
  me-textarea {
    display: block;
    margin-bottom: 20px;
  }

  &__row {
    @include above($bp-tablet) {
      display: flex;
      gap: 16px;
      margin-bottom: 20px;

      me-input,
      me-select {
        flex: 1 1 0;
        min-width: 0;
        margin-bottom: 0;
      }
    }
  }

  // ── Phone (presentational dial-code prefix overlaid on me-input) ──
  &__phone {
    position: relative;
    margin-bottom: 20px;

    me-input {
      margin-bottom: 0;
    }

    // Reserve room for the dial-code prefix inside the field.
    // .input {
    //  padding-left: 87px;
    // }
  }

  &__phone-prefix {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    height: calc(#{map-get($font-sizes, md)} * 1.5 + var(--spacing-sm) * 2 + var(--border-size-sm) * 2);
    padding: 0 8px;
    pointer-events: none;
  }

  &__phone-flag {
    width: 20px;
    height: 15px;
    background: #dbdbdb;
    box-shadow: 0 0 1px 0 #888;
  }

  &__phone-code {
    height: 24px;
    margin-left: 7px;
    padding-left: 7px;
    border-left: 1px solid $divider-color;
    color: $placeholder-color;
    font-size: 14px;
    line-height: 24px;
  }

  // ── Submit + agreement ───────────────────────────────────
  &__submit {
    display: block;
    width: 100%;
    margin-bottom: 16px;

    .me-button {
      width: 100%;
    }
  }

  &__agreement {
    margin: 0 auto;
    font-size: 12px;
    line-height: 1.67;
    color: var(--text-color-primary, #2c2c2c);
    text-align: center;

    @include above($bp-tablet) {
      max-width: 532px;
    }

    a {
      color: var(--link-color-brand-primary, #0777cf);
    }
  }
}

@keyframes #{$component-class}-fade-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes #{$component-class}-fade-out {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}

@keyframes #{$component-class}-slide-in {
  from {
    transform: translateY(20px);
  }

  to {
    transform: translateY(0);
  }
}

@keyframes #{$component-class}-slide-out {
  from {
    transform: translateY(0);
  }

  to {
    transform: translateY(-20px);
  }
}
